import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, DollarSign, TrendingUp, Clock, FileText, BarChart3, AlertTriangle } from 'lucide-react';
import { useClinic } from '@/contexts/ClinicContext';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import {
  invoicesService,
  type AgingReport,
  type InvoiceSummaryReport,
  type PracticeProductsRevenueReport,
  type InvoiceSource,
} from '@/services/invoicesService';
import { InvoiceSourceBadge } from '@/components/invoicing/InvoiceSourceBadge';
import { useNavigate } from 'react-router-dom';

const formatCents = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function InvoiceDashboardPage() {
  const { clinic } = useClinic();
  const clinicId = clinic?.id;
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  const dateRangeParams = useMemo(
    () => ({
      startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
      endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
    }),
    [startDate, endDate],
  );

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['invoice-summary', clinicId, dateRangeParams.startDate, dateRangeParams.endDate],
    queryFn: () => invoicesService.getSummaryReport(clinicId!, dateRangeParams),
    enabled: !!clinicId,
  });

  const { data: aging, isLoading: agingLoading } = useQuery({
    queryKey: ['invoice-aging', clinicId],
    queryFn: () => invoicesService.getAgingReport(clinicId!),
    enabled: !!clinicId,
  });

  const { data: productRevenue, isLoading: productLoading } = useQuery({
    queryKey: ['product-revenue', clinicId, dateRangeParams.startDate, dateRangeParams.endDate],
    queryFn: () => invoicesService.getPracticeProductsRevenue(clinicId!, dateRangeParams),
    enabled: !!clinicId,
  });

  if (!clinicId) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Select a clinic to view invoice reports.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Invoice Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Overview of invoicing metrics and aging reports
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DatePicker
            value={startDate}
            onValueChange={setStartDate}
            maxDate={endDate}
            className="w-40"
          />
          <span className="text-muted-foreground">to</span>
          <DatePicker
            value={endDate}
            onValueChange={setEndDate}
            minDate={startDate}
            className="w-40"
          />
          <Button variant="outline" onClick={() => navigate('/invoices')}>
            <FileText className="mr-2 h-4 w-4" />
            All Invoices
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {summaryLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <DollarSign className="h-4 w-4" />
                Total Invoiced
              </div>
              <p className="text-2xl font-bold mt-1">{formatCents(summary.invoicedTotalCents)}</p>
              <p className="text-xs text-muted-foreground">{summary.totalInvoices} invoices</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <TrendingUp className="h-4 w-4" />
                Collected
              </div>
              <p className="text-2xl font-bold mt-1">{formatCents(summary.collectedTotalCents)}</p>
              <p className="text-xs text-muted-foreground">{summary.collectionRate}% collection rate</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <FileText className="h-4 w-4" />
                Open
              </div>
              <p className="text-2xl font-bold mt-1">{summary.openInvoices}</p>
              <p className="text-xs text-muted-foreground">{summary.paidInvoices} paid</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Clock className="h-4 w-4" />
                Avg Days to Pay
              </div>
              <p className="text-2xl font-bold mt-1">
                {summary.avgDaysToPayment !== null ? `${summary.avgDaysToPayment}d` : 'N/A'}
              </p>
            </CardContent>
          </Card>
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Aging Report */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Invoice Aging
            </CardTitle>
          </CardHeader>
          <CardContent>
            {agingLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : aging ? (
              <div className="space-y-3">
                {aging.buckets.map((bucket) => (
                  <div
                    key={bucket.bucket}
                    className="flex items-center justify-between p-3 rounded-md bg-muted/50"
                  >
                    <div>
                      <p className="font-medium text-sm">{bucket.bucket}</p>
                      <p className="text-xs text-muted-foreground">{bucket.count} invoices</p>
                    </div>
                    <p className="font-bold">{formatCents(bucket.totalBalanceCents)}</p>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2 border-t">
                  <p className="font-semibold">Total Outstanding</p>
                  <p className="font-bold text-lg">{formatCents(aging.grandTotalBalance)}</p>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No data available</p>
            )}
          </CardContent>
        </Card>

        {/* Top Products by Revenue */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-500" />
              Top Products by Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            {productLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : productRevenue && productRevenue.products.length > 0 ? (
              <div className="space-y-2">
                {productRevenue.products.slice(0, 8).map((product, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{product.description}</p>
                      <div className="flex gap-2 mt-0.5">
                        {product.cptCode && (
                          <Badge variant="outline" className="text-xs">
                            {product.cptCode}
                          </Badge>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {product.invoiceCount} invoices | {product.revenuePercent}%
                        </span>
                      </div>
                    </div>
                    <p className="font-semibold text-sm ml-4">
                      {formatCents(product.totalRevenueCents)}
                    </p>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2 border-t">
                  <p className="font-semibold">Total</p>
                  <p className="font-bold">{formatCents(productRevenue.totalRevenueCents)}</p>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No product revenue data</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Status Breakdown */}
      {summary?.statusBreakdown && (
        <Card>
          <CardHeader>
            <CardTitle>Invoice Status Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
              {Object.entries(summary.statusBreakdown).map(([status, data]) => (
                <div key={status} className="text-center p-3 rounded-md bg-muted/50">
                  <Badge
                    variant={
                      status === 'paid'
                        ? 'default'
                        : status === 'void'
                          ? 'destructive'
                          : 'secondary'
                    }
                    className="mb-2"
                  >
                    {status}
                  </Badge>
                  <p className="text-lg font-bold">{data.count}</p>
                  <p className="text-xs text-muted-foreground">{formatCents(data.totalCents)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Source Breakdown */}
      {summary?.sourceBreakdown && Object.keys(summary.sourceBreakdown).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Revenue by Source</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
              {Object.entries(summary.sourceBreakdown).map(([source, data]) => (
                <div key={source} className="flex flex-col items-center text-center p-3 rounded-md bg-muted/50">
                  <div className="mb-2">
                    <InvoiceSourceBadge source={source as InvoiceSource} showContext={false} />
                  </div>
                  <p className="text-lg font-bold">{data.count}</p>
                  <p className="text-xs text-muted-foreground">{formatCents(data.totalCents)}</p>
                  {data.paidCents > 0 && (
                    <p className="text-xs text-green-600 mt-0.5">
                      {formatCents(data.paidCents)} paid
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
